import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { TalentProfileCompleteness } from "../../../../../api/dashboard/dashboard.types";
import { Subject, take, takeUntil } from "rxjs";
import { CmsService } from "../../../../../api/cms/cms.service";
import { TranslateService } from "@ngx-translate/core";
import { LocaleService } from "@momhunting/core";

@Component({
  selector: "mh-talent-dashboard-status-box",
  templateUrl: "./talent-dashboard-status-box.component.html",
  styleUrls: ["./talent-dashboard-status-box.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TalentDashboardStatusBoxComponent implements OnInit, OnDestroy, OnChanges {
  @Input() completeness!: TalentProfileCompleteness;
  content: string | undefined = '';
  destroy$ = new Subject<void>();

  constructor(
    private readonly cmsService: CmsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly localeService: LocaleService,
  ) {}

  ngOnInit() {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.loadCmsContent();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['completeness']?.currentValue) {
      this.loadCmsContent();
    }
  }

  loadCmsContent() {
    if (!this.completeness) {
      return;
    }
    const boxName = this.resolveBoxName();

    this.cmsService.loadBox(boxName, this.localeService.currentLocale)
      .pipe(take(1))
      .subscribe((content) => {
        this.content = boxName !== 'TalentDashboardProfileComplete' ? this.generateMissingFieldsHTML(content) : content;
        this.changeDetectorRef.markForCheck();
      });
  }

  private resolveBoxName() {
    if (+this.completeness.completenessPercentage === 100 && this.completeness.isProfileFilledOut) {
      return 'TalentDashboardProfileComplete';
    } else if (this.completeness.isProfileFilledOut) {
      return 'TalentDashboardProfilePartial';
    } else {
      return 'TalentDashboardProfileIncomplete';
    }
  }

  private generateMissingFieldsHTML(content: string): string {
    let missingFieldsList = this.completeness.sections.flatMap((section) => section.unfilledOutFields);

    if (!missingFieldsList.length) {
      return content.replace(/\{\{\s*missingFieldsList(\s*\|\s*\{limit:\s*\d+})?\s*}}/g, '');
    }

    const limitMatch = content.match(/\{\{\s*missingFieldsList\s*\|\s*\{limit:\s*(\d+)}\s*}}/);
    if (limitMatch && limitMatch[1]) {
      missingFieldsList = missingFieldsList.slice(0, +limitMatch[1]);
    }

    const missingFieldsHTML = `<ul class="missing-fields-list">${missingFieldsList.map((field) => {
      const missingKey = 'profile.missing.' + field;
      const translated = this.translateService.instant(missingKey);
      if (translated && translated !== missingKey) {
        return `<li>${translated}</li>`;
      }

      const subField = field.includes('.') ? field.split('.').pop()! : field;
      const kebab = subField.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      const fallbackKeys = [
        `talent.profile.${subField}.label`,
        `profile.${kebab}.label`,
        `profile.personality-${kebab}.label`,
        `profile.${subField}.label`,
        `talent.profile.${field}.label`
      ];

      for (const key of fallbackKeys) {
        const fallbackTranslated = this.translateService.instant(key);
        if (fallbackTranslated && fallbackTranslated !== key) {
          return `<li>${fallbackTranslated}</li>`;
        }
      }

      return `<li>${field}</li>`;
    }).join('')}</ul>`;

    const placeholderRegex = /\{\{\s*missingFieldsList(\s*\|\s*\{limit:\s*\d+})?\s*}}/g;
    if (placeholderRegex.test(content)) {
      return content.replace(placeholderRegex, missingFieldsHTML);
    }

    const isDe = this.localeService.currentLocale === 'de';
    const introText = isDe
      ? '<p>Um Dein Profil zu vervollständigen, fehlen noch folgende Angaben:</p>'
      : '<p>To complete your profile, the following information is still missing:</p>';

    // Position on top: right after the first paragraph (welcome message)
    const firstPCloseIndex = content.indexOf('</p>');
    if (firstPCloseIndex !== -1) {
      const insertPos = firstPCloseIndex + 4;
      return content.slice(0, insertPos) + introText + missingFieldsHTML + content.slice(insertPos);
    }

    return `${introText}${missingFieldsHTML}${content}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }
}
